import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Input, Label, Spinner, Modal, ModalBody, Form, FormGroup } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../../AbstractElements';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../../../Config/api';
import authHeader from '../../../Customer/authHeader';
import { COUNTRIES } from '../../../../Constant/countries';

const today = () => new Date().toISOString().split('T')[0];
const SHIPPING_COST = 50;

// Order currencies. Each is priced from its own product column — prices are
// never converted from one currency to another.
const CURRENCIES = [
  { code: 'EUR', symbol: '€', priceField: 'wholesale_price' },
  { code: 'USD', symbol: '$', priceField: 'wholesale_price_usd' },
  { code: 'GBP', symbol: '£', priceField: 'wholesale_price_gbp' },
];
const currencyMeta = (code) => CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

// Normalise mixed-case catalog text (some titles are UPPERCASE, some Title Case) to a
// consistent Title Case for display.
const titleCase = (s) => (s || '')
  .toLowerCase()
  .replace(/(^|[\s\-/])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

// On-brand placeholder (a boutique dress in line-art) shown when a product has no photo.
const ProductPlaceholder = ({ size = 46 }) => (
  <div style={{
    width: size, height: size, borderRadius: 8,
    background: 'linear-gradient(135deg, #eef0fe 0%, #eef1f8 100%)',
    border: '1px solid var(--rf-border, #e7eaf3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }} title="No image">
    <svg width={size * 0.62} height={size * 0.62} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M17 12.5 L24 16 L31 12.5 L34.5 18.5 L29 21.5 L32 37 L16 37 L19 21.5 L13.5 18.5 Z"
        fill="#dcdcfb" stroke="#8b88e6" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M24 16 L24 21" stroke="#8b88e6" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
    </svg>
  </div>
);

// Small inline thumbnail; falls back to the placeholder, click to enlarge.
const Thumb = ({ src, alt, onOpen }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <ProductPlaceholder size={46} />;
  }
  return (
    <img src={src} alt={alt} loading="lazy" width={46} height={46}
      onError={() => setFailed(true)}
      onClick={onOpen}
      title="Click to enlarge"
      style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--rf-border, #e7eaf3)', cursor: onOpen ? 'zoom-in' : 'default' }} />
  );
};

const emptyNewCustomer = {
  store_name: '', vat_eori: '', email: '', tel: '',
  shipping_address: '', billing_address: '', country: '', showroom: '', extra_info: '',
};

const NewOrder = () => {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [customerMode, setCustomerMode] = useState('existing'); // 'existing' | 'new'
  const [customerId, setCustomerId] = useState('');
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [loadingStores, setLoadingStores] = useState(false);

  const [newCustomer, setNewCustomer] = useState(emptyNewCustomer);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const [orderDate, setOrderDate] = useState(today());
  const [deliveryDate, setDeliveryDate] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [filter, setFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { title, sku, images:[], index }

  // Which of the company's own VAT registrations (e.g. EU/NL vs UK) to quote
  // on this order's invoice — admin-configurable under Settings, never
  // hardcoded here.
  const [companyVatNumbers, setCompanyVatNumbers] = useState([]);
  const [companyVatNumberId, setCompanyVatNumberId] = useState('');

  // Same idea for the company's own EORI registrations.
  const [companyEoriNumbers, setCompanyEoriNumbers] = useState([]);
  const [companyEoriNumberId, setCompanyEoriNumberId] = useState('');

  // Draft country/VAT for an existing customer that has no delivery country
  // saved yet — lets the user set it here instead of blocking on "Not set".
  const [countryDraft, setCountryDraft] = useState('');
  const [vatDraft, setVatDraft] = useState('');
  const [savingCountry, setSavingCountry] = useState(false);

  // Delivery country: the selected existing customer's country, or the one
  // being entered for a new customer.
  const deliveryCountry = customerMode === 'existing'
    ? (customers.find((c) => String(c.id) === String(customerId))?.country || '')
    : newCustomer.country;

  const openLightbox = (it) => {
    const imgs = (it.images && it.images.length ? it.images : (it.image ? [it.image] : []));
    if (!imgs.length) return;
    setLightbox({ title: it.title, sku: it.sku, images: imgs, index: 0 });
  };
  const stepLightbox = (dir) => setLightbox((lb) => {
    if (!lb) return lb;
    const n = lb.images.length;
    return { ...lb, index: (lb.index + dir + n) % n };
  });

  useEffect(() => {
    (async () => {
      try {
        const [customersRes, productsRes, companyVatNumbersRes, companyEoriNumbersRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/customers`, authHeader()),
          axios.get(`${API_BASE_URL}/api/product-list`),
          axios.get(`${API_BASE_URL}/api/company-vat-numbers`),
          axios.get(`${API_BASE_URL}/api/company-eori-numbers`),
        ]);
        setCustomers(customersRes.data || []);
        const companyVatOpts = companyVatNumbersRes.data.options || [];
        setCompanyVatNumbers(companyVatOpts);
        if (companyVatOpts.length) setCompanyVatNumberId(String(companyVatOpts[0].id));
        const companyEoriOpts = companyEoriNumbersRes.data.options || [];
        setCompanyEoriNumbers(companyEoriOpts);
        if (companyEoriOpts.length) setCompanyEoriNumberId(String(companyEoriOpts[0].id));

        const mapped = (productsRes.data.products || []).map((p) => ({
          id: p.id,
          title: p.title || '',
          sku: p.sku || '',
          collection: p.collection || '',
          colour: p.colour || '',
          fabric: p.fabric || '',
          image: p.image || null,
          images: (p.images || []).map((i) => i.image_url).filter(Boolean),
          wholesale_price: parseFloat(p.wholesale_price || 0),
          wholesale_price_usd: parseFloat(p.wholesale_price_usd || 0),
          wholesale_price_gbp: parseFloat(p.wholesale_price_gbp || 0),
          s_qty: 0,
          m_qty: 0,
        }));
        mapped.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) ||
          a.sku.localeCompare(b.sku, undefined, { sensitivity: 'base' }));
        setProducts(mapped);
      } catch (e) {
        console.error('Failed to load new order form', e);
        toast.error('Failed to load the order form');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!customerId || customerMode !== 'existing') { setStores([]); setStoreId(''); return; }
    (async () => {
      setLoadingStores(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customers/${customerId}/stores`, authHeader());
        const sList = res.data.stores || [];
        setStores(sList);
        setStoreId(sList.length === 1 ? String(sList[0].id) : '');
      } catch (e) {
        console.error('Failed to load stores', e);
        toast.error('Failed to load stores for this customer');
      } finally {
        setLoadingStores(false);
      }
    })();
  }, [customerId, customerMode]);

  useEffect(() => {
    if (customerMode !== 'existing' || !customerId) { setCountryDraft(''); setVatDraft(''); return; }
    const c = customers.find((cust) => String(cust.id) === String(customerId));
    setCountryDraft(c?.country || '');
    setVatDraft(c?.vat_eori || '');
  }, [customerId, customerMode, customers]);

  const saveCustomerCountry = async () => {
    const c = customers.find((cust) => String(cust.id) === String(customerId));
    if (!c || !countryDraft) return;
    setSavingCountry(true);
    try {
      await axios.put(`${API_BASE_URL}/api/customers/${customerId}`, { ...c, country: countryDraft, vat_eori: vatDraft }, authHeader());
      setCustomers((prev) => prev.map((cust) => (String(cust.id) === String(customerId) ? { ...cust, country: countryDraft, vat_eori: vatDraft } : cust)));
      toast.success('Delivery country saved to customer');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save delivery country');
    } finally {
      setSavingCountry(false);
    }
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const createCustomer = async () => {
    if (!newCustomer.store_name.trim()) { toast.error('Customer/store name is required'); return; }
    setCreatingCustomer(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/customers`, newCustomer, authHeader());
      const newId = res.data.customerId;
      toast.success('Customer created');

      const [custRes, storesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/customers`, authHeader()),
        axios.get(`${API_BASE_URL}/api/customers/${newId}/stores`, authHeader()),
      ]);
      setCustomers(custRes.data || []);
      const sList = storesRes.data.stores || [];
      setStores(sList);
      setStoreId(sList.length ? String(sList[0].id) : '');
      setCustomerId(String(newId));
      setCustomerMode('existing');
      setNewCustomer(emptyNewCustomer);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Fall back to the EUR price when a product has no price in the chosen
  // currency, matching how the server prices the order.
  const sym = currencyMeta(currency).symbol;
  const priceOf = (it) => it[currencyMeta(currency).priceField] || it.wholesale_price;
  const rowQty = (it) => (Number(it.s_qty) || 0) + (Number(it.m_qty) || 0);
  const rowTotal = (it) => rowQty(it) * priceOf(it);

  const setQty = (id, field, val) => {
    setProducts((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: Math.max(0, parseInt(val || 0, 10)) } : it)));
  };

  // Unique collection names for the dropdown (sorted, case-insensitive, de-duped).
  const collections = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const c = (p.collection || '').trim();
      if (c && !map.has(c.toLowerCase())) map.set(c.toLowerCase(), c);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [products]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const col = collectionFilter.trim().toLowerCase();
    return products.filter((p) => {
      if (col && (p.collection || '').toLowerCase() !== col) return false;
      if (!q) return true;
      return [p.title, p.sku, p.colour, p.fabric, p.collection].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [products, filter, collectionFilter]);

  const chosen = products.filter((it) => rowQty(it) > 0);
  const subtotal = chosen.reduce((a, it) => a + rowTotal(it), 0);
  const totalUnits = chosen.reduce((a, it) => a + rowQty(it), 0);

  const submit = async () => {
    if (!customerId) { toast.error('Please select or create a customer'); return; }
    if (chosen.length === 0) { toast.error('Enter a quantity for at least one product'); return; }
    setSubmitting(true);
    try {
      const items = chosen.map((it) => ({ product_id: it.id, s_qty: it.s_qty, m_qty: it.m_qty }));
      const res = await axios.post(`${API_BASE_URL}/api/orders`, {
        customer_id: Number(customerId),
        store_id: storeId ? Number(storeId) : null,
        order_date: orderDate,
        preferred_delivery_date: deliveryDate || null,
        currency,
        items,
        company_vat_number_id: companyVatNumberId ? Number(companyVatNumberId) : null,
        company_eori_number_id: companyEoriNumberId ? Number(companyEoriNumberId) : null,
      });
      toast.success(`Order ${res.data.orderId} created`);
      navigate(`${process.env.PUBLIC_URL}/all-orders`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCustomerName = customers.find((c) => String(c.id) === String(customerId))?.store_name;

  return (
    <Fragment>
      <Breadcrumbs parent="Orders" title="New Order" mainTitle="New Order" />
      <Container fluid={true}>
        {loading ? (
          <Card><CardBody><Spinner size="sm" /> <span className="text-muted">Loading the collection…</span></CardBody></Card>
        ) : (
          <>
            {/* Customer selection */}
            <Card>
              <CardHeader className="pb-0"><h5>Customer</h5></CardHeader>
              <CardBody>
                <div className="d-flex gap-2 mb-3">
                  <Button size="sm" color={customerMode === 'existing' ? 'primary' : 'light'} onClick={() => setCustomerMode('existing')}>Existing Customer</Button>
                  <Button size="sm" color={customerMode === 'new' ? 'primary' : 'light'} onClick={() => setCustomerMode('new')}>New Customer</Button>
                </div>

                {customerMode === 'existing' ? (
                  <Row>
                    <Col md="4">
                      <Label className="form-label">Customer *</Label>
                      <Input type="select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mb-2">
                        <option value="">Select customer…</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.store_name}</option>)}
                      </Input>
                    </Col>
                    <Col md="4">
                      <Label className="form-label">Store</Label>
                      <Input type="select" value={storeId} onChange={(e) => setStoreId(e.target.value)} className="mb-2" disabled={!customerId || loadingStores}>
                        <option value="">{loadingStores ? 'Loading…' : (stores.length ? 'Select store…' : 'No stores')}</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </Input>
                    </Col>
                    <Col md="4">
                      <Label className="form-label">Delivery Country</Label>
                      {deliveryCountry ? (
                        <Input value={deliveryCountry} disabled className="mb-2" />
                      ) : (
                        <>
                          <Input
                            type="select" bsSize="sm" className="mb-1"
                            value={countryDraft}
                            disabled={!customerId}
                            onChange={(e) => setCountryDraft(e.target.value)}
                          >
                            <option value="">Select country…</option>
                            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </Input>
                          <Input
                            bsSize="sm" className="mb-1" placeholder="VAT/EORI number"
                            value={vatDraft}
                            disabled={!customerId}
                            onChange={(e) => setVatDraft(e.target.value)}
                          />
                          <Button
                            size="sm" color="primary" outline
                            disabled={!customerId || !countryDraft || savingCountry}
                            onClick={saveCustomerCountry}
                          >
                            {savingCountry ? 'Saving…' : 'Save to Customer'}
                          </Button>
                        </>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <Form>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Store/Customer Name *</Label>
                          <Input name="store_name" value={newCustomer.store_name} onChange={handleNewCustomerChange} placeholder="Enter store name" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>VAT/EORI</Label>
                          <Input name="vat_eori" value={newCustomer.vat_eori} onChange={handleNewCustomerChange} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Email</Label>
                          <Input type="email" name="email" value={newCustomer.email} onChange={handleNewCustomerChange} />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Tel</Label>
                          <Input name="tel" value={newCustomer.tel} onChange={handleNewCustomerChange} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>Shipping Address</Label>
                          <Input type="textarea" rows="2" name="shipping_address" value={newCustomer.shipping_address} onChange={handleNewCustomerChange} />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Billing Address</Label>
                          <Input type="textarea" rows="2" name="billing_address" value={newCustomer.billing_address} onChange={handleNewCustomerChange} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Button color="primary" size="sm" onClick={createCustomer} disabled={creatingCustomer} type="button">
                      {creatingCustomer ? 'Creating…' : 'Create Customer'}
                    </Button>
                  </Form>
                )}

                {customerMode === 'existing' && selectedCustomerName && (
                  <p className="text-muted f-12 mb-0 mt-2">Ordering for <strong>{selectedCustomerName}</strong></p>
                )}
              </CardBody>
            </Card>

            {/* Order header */}
            <Card>
              <CardHeader className="pb-0"><h5>Order Details</h5></CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <Label className="form-label">Order Date</Label>
                    <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="mb-2" />
                  </Col>
                  <Col md="4">
                    <Label className="form-label">Preferred Delivery</Label>
                    <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mb-2" />
                  </Col>
                  <Col md="4">
                    <Label className="form-label">Currency</Label>
                    <Input type="select" value={currency} onChange={(e) => setCurrency(e.target.value)} className="mb-2">
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>{`${c.code} (${c.symbol})`}</option>
                      ))}
                    </Input>
                  </Col>
                </Row>
                {(companyVatNumbers.length > 0 || companyEoriNumbers.length > 0) && (
                  <Row>
                    {companyVatNumbers.length > 0 && (
                      <Col md="4">
                        <Label className="form-label">Company VAT No.</Label>
                        <Input type="select" value={companyVatNumberId} onChange={(e) => setCompanyVatNumberId(e.target.value)} className="mb-2">
                          {companyVatNumbers.map((v) => (
                            <option key={v.id} value={v.id}>{`${v.label} (${v.vat_no})`}</option>
                          ))}
                        </Input>
                        <small className="text-muted">Which of FARA Boutique's VAT registrations to quote on this order's invoice.</small>
                      </Col>
                    )}
                    {companyEoriNumbers.length > 0 && (
                      <Col md="4">
                        <Label className="form-label">Company EORI No.</Label>
                        <Input type="select" value={companyEoriNumberId} onChange={(e) => setCompanyEoriNumberId(e.target.value)} className="mb-2">
                          {companyEoriNumbers.map((v) => (
                            <option key={v.id} value={v.id}>{`${v.label} (${v.eori_no})`}</option>
                          ))}
                        </Input>
                        <small className="text-muted">Which of FARA Boutique's EORI registrations to quote on this order's documents.</small>
                      </Col>
                    )}
                  </Row>
                )}
              </CardBody>
            </Card>

            {/* Collection grid */}
            <Card>
              <CardHeader className="pb-0 d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="mb-0">Collection</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <div style={{ minWidth: 180 }}>
                    <Input type="select" bsSize="sm" value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)}>
                      <option value="">All collections</option>
                      {collections.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Input>
                  </div>
                  <div style={{ minWidth: 240 }}>
                    <Input bsSize="sm" value={filter} placeholder="Filter by title, SKU, colour, fabric…"
                      onChange={(e) => setFilter(e.target.value)} />
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table className="align-middle" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 56 }}></th>
                        <th>Collection</th>
                        <th>Title</th>
                        <th>Colour</th>
                        <th>Fabric</th>
                        <th>SKU</th>
                        <th className="text-end">Price</th>
                        <th style={{ width: 80 }} className="text-center">SM</th>
                        <th style={{ width: 80 }} className="text-center">ML</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 ? (
                        <tr><td colSpan="11" className="text-center text-muted py-3">No products match "{filter}".</td></tr>
                      ) : visible.map((it) => {
                        const active = rowQty(it) > 0;
                        return (
                          <tr key={it.id} style={active ? { background: 'var(--rf-accent-soft, #eef0fe)' } : undefined}>
                            <td><Thumb src={it.image} alt={it.title} onOpen={() => openLightbox(it)} /></td>
                            <td>{it.collection || '-'}</td>
                            <td className="fw-semibold">{titleCase(it.title)}</td>
                            <td>{it.colour ? titleCase(it.colour) : '-'}</td>
                            <td>{it.fabric || '-'}</td>
                            <td className="text-muted">{it.sku}</td>
                            <td className="text-end">{sym}{priceOf(it).toFixed(2)}</td>
                            <td>
                              <Input bsSize="sm" type="number" min="0" className="text-center" value={it.s_qty}
                                onChange={(e) => setQty(it.id, 's_qty', e.target.value)} />
                            </td>
                            <td>
                              <Input bsSize="sm" type="number" min="0" className="text-center" value={it.m_qty}
                                onChange={(e) => setQty(it.id, 'm_qty', e.target.value)} />
                            </td>
                            <td className="text-center">{rowQty(it)}</td>
                            <td className="text-end">{sym}{rowTotal(it).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </Container>

      {/* Sticky order summary bar */}
      {!loading && (
        <div style={{
          position: 'sticky', bottom: 0, zIndex: 5,
          background: 'var(--rf-card, #fff)', borderTop: '1px solid var(--rf-border, #e7eaf3)',
          boxShadow: '0 -2px 10px rgba(20,28,56,.06)', padding: '12px 24px',
        }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex gap-4 flex-wrap">
            <span className="text-muted f-12">Items: <strong className="text-dark">{chosen.length}</strong> · Units: <strong className="text-dark">{totalUnits}</strong></span>
            <span className="text-muted f-12">Subtotal: <strong className="text-dark">{sym}{subtotal.toFixed(2)}</strong></span>
            <span className="text-muted f-12">Shipping: <strong className="text-dark">{sym}{SHIPPING_COST.toFixed(2)}</strong></span>
            <span>Total: <strong>{sym}{(subtotal + SHIPPING_COST).toFixed(2)}</strong></span>
          </div>
          <div className="d-flex gap-2">
            <Button color="light" onClick={() => navigate(`${process.env.PUBLIC_URL}/all-orders`)} disabled={submitting}>Cancel</Button>
            <Button color="primary" onClick={submit} disabled={submitting || !customerId || chosen.length === 0}>
              {submitting ? 'Creating…' : 'Create Order'}
            </Button>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      <Modal isOpen={!!lightbox} toggle={() => setLightbox(null)} centered size="lg">
        <ModalBody className="p-0 position-relative" style={{ background: '#0f1117' }}>
          <Button close onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 10, right: 14, zIndex: 2, filter: 'invert(1)' }} />
          {lightbox && (
            <div className="text-center">
              <img src={lightbox.images[lightbox.index]} alt={lightbox.title}
                style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />
              {lightbox.images.length > 1 && (
                <>
                  <Button color="light" onClick={() => stepLightbox(-1)}
                    style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', opacity: 0.9 }}>‹</Button>
                  <Button color="light" onClick={() => stepLightbox(1)}
                    style={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', opacity: 0.9 }}>›</Button>
                </>
              )}
              <div className="text-white d-flex justify-content-between align-items-center px-3 py-2" style={{ fontSize: 13 }}>
                <span><strong>{titleCase(lightbox.title)}</strong> <span className="text-muted">· {lightbox.sku}</span></span>
                {lightbox.images.length > 1 && <span className="text-muted">{lightbox.index + 1} / {lightbox.images.length}</span>}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default NewOrder;
